import { ExpandLess, ExpandMore, WarningAmber } from '@mui/icons-material';
import { Avatar, Box, Grid, IconButton } from '@mui/material';
import _ from 'lodash';
import React, { ReactNode, useMemo, useState } from 'react';
import DrawerLink from '../../components/common/drawers/ModuleDrawer/DrawerLink';
import { EmployeeTimeOffAvailabilityRecord } from '../../types/EmployeeTimeOff';
import { UserConfigurableModule } from '../../types/User';
import { buildEventRows, getAvailabilityConfigFromRecords, overlappingEvents } from '../../utility/availability';
import { IEventData } from './EmployeesSchedule';
import {
  borderStyle,
  DAY_COUNT,
  scheduleAvailabilityDotCenteredSx,
  scheduleConflictIconSx,
  scheduleDayBackgroundSx,
  scheduleEventTileContainerSx,
  scheduleGridContainerSx,
  scheduleRowAvatarSx,
  scheduleRowContainerSx,
  scheduleRowEmployeeCellSx,
  scheduleRowEmployeeContainerSx,
  scheduleRowEmployeeHeaderSx,
  scheduleRowExpandButtonSx,
  scheduleRowExpandContainerSx,
} from './employeesScheduleStyles';
import ScheduleRowTile from './ScheduleRowTile';

export interface IScheduleRowProps {
  employeeUuid: string;
  employeeLabel: string;
  employeeAvatarUrl?: string;
  events: IEventData[];
  weekDateKeys: string[];
  availabilityByDate: Record<string, EmployeeTimeOffAvailabilityRecord[]>;
}

export default function ScheduleRow({ employeeUuid, employeeLabel, employeeAvatarUrl, events, weekDateKeys, availabilityByDate }: IScheduleRowProps) {
  const [collapsed, setCollapsed] = useState(true);

  const eventRows = useMemo(() => buildEventRows(events), [events]);

  const visibleEventRows = collapsed ? _.take(eventRows, 1) : eventRows;

  const hasOverlappingEvents = useMemo(() => overlappingEvents(events), [events]);

  const renderDayBackgrounds = (): ReactNode =>
    _.map(weekDateKeys, (dateKey, index) => {
      const records = availabilityByDate[dateKey];
      const { backgroundColor, color } = getAvailabilityConfigFromRecords(records);

      const dayCol = index + 1;

      const hasEventOnDay = _.some(visibleEventRows, (row) => _.some(row, (eventData) => dayCol >= eventData.startCol && dayCol < eventData.endCol));

      const showConflictIcon = hasEventOnDay && !_.isEmpty(records);

      return (
        <Box
          key={dateKey}
          sx={{
            ...scheduleDayBackgroundSx,
            gridColumn: `${dayCol} / span 1`,
            gridRow: `1 / span ${Math.max(visibleEventRows.length, 1)}`,
            borderRight: index === DAY_COUNT - 1 ? 'none' : borderStyle,
            backgroundColor,
          }}
        >
          {showConflictIcon ? (
            <WarningAmber
              sx={{
                ...scheduleConflictIconSx,
                color,
              }}
            />
          ) : (
            <Box
              sx={{
                ...scheduleAvailabilityDotCenteredSx,
                bgcolor: color,
              }}
            />
          )}
        </Box>
      );
    });

  const renderEvents = (): ReactNode =>
    _.flatMap(visibleEventRows, (row, rowIndex) =>
      _.map(row, (eventData) => (
        <Box
          key={`${eventData.event.uuid}-${rowIndex}`}
          sx={{
            ...scheduleEventTileContainerSx,
            gridColumn: `${eventData.startCol} / ${eventData.endCol}`,
            gridRow: rowIndex + 1,
          }}
        >
          <ScheduleRowTile event={eventData.event} attendance={eventData.attendance} />
        </Box>
      ))
    );

  return (
    <Grid container columns={8} size={8} sx={scheduleRowContainerSx}>
      <Grid size={1} sx={scheduleRowEmployeeCellSx}>
        <Box sx={scheduleRowEmployeeContainerSx}>
          <Box sx={scheduleRowEmployeeHeaderSx}>
            <Avatar src={employeeAvatarUrl} alt="avatar" sx={scheduleRowAvatarSx} />

            <DrawerLink module={UserConfigurableModule.EMPLOYEES} uuid={employeeUuid}>
              {employeeLabel}
            </DrawerLink>
          </Box>

          <Box sx={scheduleRowExpandContainerSx}>
            {hasOverlappingEvents && (
              <IconButton onClick={() => setCollapsed(!collapsed)} sx={scheduleRowExpandButtonSx}>
                {collapsed ? <ExpandMore /> : <ExpandLess />}
              </IconButton>
            )}
          </Box>
        </Box>
      </Grid>

      <Grid size={7}>
        <Box sx={scheduleGridContainerSx}>
          {renderDayBackgrounds()}
          {renderEvents()}
        </Box>
      </Grid>
    </Grid>
  );
}
